import { ContestRewardType } from './contestTriggerShared';

export function newContestTriggerV2ClientId(): string {
  return `ct2_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

export interface ContestTriggerV2BranchRow {
  id: string
  branchName: string
  stepIndex: number
}

export interface ContestTriggerV2EpisodeCard {
  id: string
  episodeUuid: string
  branches: ContestTriggerV2BranchRow[]
}

export interface ContestTriggerV2FormValues {
  /** Internal identifier (maps to contests_v2.trigger_identifier). */
  triggerIdentifier: string
  episodes: ContestTriggerV2EpisodeCard[]
  title: string
  message: string
  rewardType: ContestRewardType
  rewardAmount: number
}

export const CONTEST_TRIGGER_V2_MAX = {
  triggerIdentifier: 64,
  branchName: 64,
  title: 100,
  message: 255,
} as const;

export function createEmptyContestTriggerV2Branch(): ContestTriggerV2BranchRow {
  return {
    id: newContestTriggerV2ClientId(),
    branchName: '',
    stepIndex: 0,
  };
}

export function createEmptyContestTriggerV2Episode(): ContestTriggerV2EpisodeCard {
  return {
    id: newContestTriggerV2ClientId(),
    episodeUuid: '',
    branches: [createEmptyContestTriggerV2Branch()],
  };
}

export function createEmptyContestTriggerV2Values(): ContestTriggerV2FormValues {
  return {
    triggerIdentifier: '',
    episodes: [],
    title: '',
    message: '',
    rewardType: ContestRewardType.Sapphires,
    rewardAmount: 0,
  };
}

/** Map admin API amounts (from contests_v2.amounts) into V2 form reward fields. */
export function v2RewardFieldsFromApiAmounts(
  sapphiresAmount: number,
  heartsAmount: number,
): Pick<ContestTriggerV2FormValues, 'rewardType' | 'rewardAmount'> {
  const s = Number(sapphiresAmount) || 0;
  const h = Number(heartsAmount) || 0;
  if (s > 0) return { rewardType: ContestRewardType.Sapphires, rewardAmount: s };
  if (h > 0) return { rewardType: ContestRewardType.Hearts, rewardAmount: h };
  return { rewardType: ContestRewardType.Sapphires, rewardAmount: 0 };
}

export function isContestTriggerV2Populated(v: ContestTriggerV2FormValues): boolean {
  if (String(v.triggerIdentifier ?? '').trim()) return true;
  if (String(v.title ?? '').trim() || String(v.message ?? '').trim()) return true;
  if (Number(v.rewardAmount) > 0) return true;
  for (const ep of v.episodes ?? []) {
    if (String(ep.episodeUuid ?? '').trim()) return true;
    for (const b of ep.branches ?? []) {
      if (String(b.branchName ?? '').trim()) return true;
    }
  }
  return false;
}

/**
 * contests_v2.trigger_data: episode UUID keys → { branchName: stepIndex }.
 */
export function buildTriggerDataFromV2Episodes(
  episodes: ContestTriggerV2EpisodeCard[],
): Record<string, Record<string, number>> {
  const out: Record<string, Record<string, number>> = {};
  for (const ep of episodes) {
    const uuid = String(ep.episodeUuid ?? '').trim();
    if (!uuid) continue;
    const branchMap: Record<string, number> = {};
    for (const b of ep.branches ?? []) {
      const bn = String(b.branchName ?? '').trim();
      if (!bn) continue;
      branchMap[bn] = Number(b.stepIndex) || 0;
    }
    if (Object.keys(branchMap).length > 0) {
      out[uuid] = branchMap;
    }
  }
  return out;
}

/** contests_v2.amounts JSON (sapphires branch includes engagementTypeId: 5). */
export function buildAmountsJsonFromV2Form(
  rewardType: ContestRewardType,
  rewardAmount: number,
): Record<string, number> {
  const out: Record<string, number> = {};
  const n = Number(rewardAmount) || 0;
  if (n <= 0) return out;
  if (rewardType === ContestRewardType.Hearts) {
    out.hearts = n;
  } else {
    out.sapphires = n;
    out.engagementTypeId = 5;
  }
  return out;
}
